import { BackArrow } from "assets/icons";
import Anchor from "components/BaseWidgets/anchor";
import BasicProfileCard from "components/Cards/BasicProfileCard";
import OverviewBlock from "components/CustomWidgets/OverviewBlock";
import Button from "components/BaseWidgets/button";
import ProfileCard from "components/Cards/ProfileCard";
import { useEffect, useState } from "react";
import JourneysCarousel from "../CustomWidgets/JourneysCarousel";
import AboutPanel from "./components/AboutPanel";
import CareerPanel from "./components/CareerPanel";
import ContactPanel from "./components/ContactPanel";
import FamilyPanel from "./components/FamilyPanel";
import InterestsPanel from "./components/InterestsPanel";
import PhysicalAppearancePanel from "./components/PhysicalAppearancePanel";
import ProfileMatch from "./components/ProfileMatch";
import { anchorItems, fieldLabels } from "./helper";
import "./style.scss";
import { usePremium } from "context/PremiumContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  useMyProfile,
  useProfileMatch,
  useSimiliarMatches,
} from "hooks/api/useProfile";
import Skeleton from "components/BaseWidgets/skeleton";
import { useSuccessStories } from "hooks/api/useHome";
import { useContext } from "react";
import { ExploreContext } from "context/ExploreContext";

function PersonProfile() {
  const { isPremium } = usePremium();
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLock, setLock] = useState(true);
  const { setTabActive, setSelectedList } = useContext(ExploreContext);

  const preview = id === "preview" ? true : false;

  const { data: profileData, isLoading: profileLoading } = useMyProfile(
    preview ? undefined : id
  );
  const fetchedUser = profileData?.user;
  const user_id = profileData?.user?.id;
  const isPrivateIMage =
    profileData?.user?.image_count != 0
      ? profileData?.user?.is_private &&
        profileData?.user?.allow_image_view === false
      : false;
  const { data: matchData } = useProfileMatch(user_id);

  useEffect(() => {
    setLock(profileData?.user?.is_locked);
  }, [profileData?.user?.is_locked]);

  const { data: similiarMatches, isLoading: isSimiliarMatches } =
    useSimiliarMatches({
      see_all: false,
      limit: 4,
      page: 1,
      profile_id: profileData?.user?.aec_id
        ? profileData?.user?.aec_id
        : undefined,
    });

  const { data: stories = [], isLoading: isStoriesLoading } = useSuccessStories(
    {
      page: 1,
      limit: 6,
      see_all: false,
    }
  );

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 250;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  const blogCardContent = (
    <>
      {similiarMatches?.users_data?.map((user) => (
        <BasicProfileCard
          profile={user}
          key={user.id}
          snippetProfileId={user.id}
          snippetProfileName={user.name}
          snippetImage={user.profile_image}
          snippetProfileAge={user.age}
          snippetProfileHeight={user.height}
          snippetProfileJob={user.profession}
          snipperVerification={user?.verification_type}
          snippetVisible={user.is_private && !user.allow_image_view}
          snippetInterestSent={user.has_expressed_interest}
          snippetFav={user.has_favorited}
          snippetPremium={user.is_premium_member}
          snippetRecommended={user.isSuggest}
          snippetImageRequested={user?.image_requested}
          isNotInterested={user.has_expressed_not_interest}
          aec_id={user?.aec_id}
        />
      ))}
    </>
  );

  const blurredFields = !preview
    ? (profileData?.blur_fields || [])
        .flatMap((key) => [fieldLabels[key], key])
        .filter(Boolean)
    : [];

  return (
    <div className="person-profile-frame">
      <div className="body-container">
        <div className="flex items-center justify-between mb-4">
          <Button
            onClick={() => {
              const savedPath = sessionStorage.getItem("profileListPath");
              if (savedPath) {
                navigate(savedPath, { state: { fromProfileDetail: true } });
              } else {
                navigate(-1);
              }
            }}
            size="none"
            className="back-btn"
          >
            <BackArrow />
          </Button>
        </div>
        {/* profile card */}
        {profileLoading ? (
          <Skeleton label={false} height={232} />
        ) : (
          fetchedUser && (
            <ProfileCard
              profile={fetchedUser}
              snippetProfileId={fetchedUser.id}
              profileImage={profileData.user_images}
              userName={fetchedUser.name}
              religion={fetchedUser.religion?.name}
              imageCount={fetchedUser.image_count}
              profession={fetchedUser.profession?.name}
              lastSeen={fetchedUser.last_seen}
              maritalStatus={fetchedUser.marital_status?.name}
              livesIn={fetchedUser.location}
              primaryLanguage={fetchedUser.primary_language?.name}
              createdBy={fetchedUser.created_by}
              userHeight={fetchedUser.height}
              profileId={fetchedUser.aec_id}
              userAge={fetchedUser.age}
              verificationType={fetchedUser.verification_type}
              isPremium={fetchedUser.is_premium_member}
              isInterested={fetchedUser.has_expressed_interest}
              isFavorite={fetchedUser.has_favorited}
              isNotInterested={fetchedUser.has_expressed_not_interest}
              wrapperClass="mb-16"
              isPrivate={isPrivateIMage}
              shareUrl={fetchedUser?.share_url}
              isImageRequested={fetchedUser.image_requested}
              redirect={false}
              preview={preview}
              aec_id={fetchedUser.aec_id}
            />
          )
        )}
        {/* anchorItems */}
        <div className={`sticky-anchor ${scrolled ? "scrolled" : ""}`}>
          <Anchor
            className={`person-profile-anchor`}
            direction="horizontal"
            items={anchorItems}
            targetOffset={150}
          />
        </div>
        <div className="content-frame flex flex-col gap-5 mb-16">
          <AboutPanel
            blurredField={blurredFields}
            id="basic"
            hasPremium={isPremium}
            profile={profileData}
            preview={preview}
          />
          <InterestsPanel
            id="interests-personality"
            interests={profileData?.interests || []}
            personality={profileData?.personality || []}
            isLock={isLock}
          />
          <ContactPanel
            blurredField={blurredFields}
            userId={user_id}
            id="contact"
            hasPremium={isPremium}
            contactDetails={profileData}
            isLock={isLock}
            preview={preview}
          />
          <CareerPanel
            id="career"
            careerDetails={profileData}
            isLock={isLock}
            blurredField={blurredFields}
          />
          <PhysicalAppearancePanel
            id="physical"
            hasPremium={isPremium}
            physicalDetails={profileData}
            isLock={isLock}
            blurredField={blurredFields}
          />
          <FamilyPanel
            id="family"
            familyDetails={profileData}
            isLock={isLock}
            blurredField={blurredFields}
          />
          <ProfileMatch
            id="partner-preferences"
            matchData={matchData?.profile_match}
            image={fetchedUser?.profile_image}
            isLock={isLock}
            preview={preview}
          />
        </div>
        <OverviewBlock
          seeAllBtn
          seeAllClick={() => {
            setSelectedList(4);
            setTabActive("7");
            navigate(
              `/explore?tab=7&profile_id=${profileData?.user?.aec_id ?? ""}`,
              {
                state: { fromProfileDetail: true },
              },
            );
          }}
          cardTitle={"Similar profile"}
          cardDesc={"Explore similar profiles with qualities and preferences"}
          cardComponent={blogCardContent}
          className="mb-16"
          data={similiarMatches?.users_data || []}
          isLoading={isSimiliarMatches}
        />
        {/* carousel */}
        {!isStoriesLoading && (
          <JourneysCarousel
            successStories={stories}
            carousalResponsive={[
              {
                breakpoint: 1427,
                settings: {
                  slidesToShow: 1.5,
                },
              },
              {
                breakpoint: 1277,
                settings: {
                  slidesToShow: 1.2,
                },
              },
              {
                breakpoint: 1064,
                settings: {
                  slidesToShow: 1,
                },
              },
              {
                breakpoint: 920,
                settings: {
                  slidesToShow: 0.7,
                },
              },
              {
                breakpoint: 820,
                settings: {
                  slidesToShow: 1.2,
                },
              },
              {
                breakpoint: 606,
                settings: {
                  slidesToShow: 1,
                },
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}

export default PersonProfile;
